import React from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import CategoryIcon from '@mui/icons-material/Category';
import ImageIcon from '@mui/icons-material/Image';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import config from '../config';
import { useAuth } from '../auth/AuthContext';
import {
  ResizableColGroup,
  ResizableHeaderCell,
  TableEmptyRow,
  TableLoadingRow,
  useResizableColumns,
} from '../components/table/ResizableTable';
import DetailModalShell from '../components/detail/DetailModalShell';
import JsonTreeViewer from '../components/detail/JsonTreeViewer';

const debounceDelay = 300;
const defaultRowsPerPage = 25;

const TABLE_COLUMNS = [
  { key: 'select', label: '', minWidth: 56, maxWidth: 90, defaultWidth: 56, resizable: false },
  { key: 'preview', label: 'Preview', minWidth: 110, maxWidth: 180, defaultWidth: 120, resizable: false },
  { key: 'title', label: 'Title', minWidth: 260, maxWidth: 720, defaultWidth: 360 },
  { key: 'tags', label: 'Tags', minWidth: 180, maxWidth: 420, defaultWidth: 240 },
  { key: 'product_type', label: 'Product Type', minWidth: 170, maxWidth: 360, defaultWidth: 210 },
  { key: 'source_shop', label: 'Source Shop', minWidth: 190, maxWidth: 420, defaultWidth: 230 },
  { key: 'target_shop', label: 'Target Shop', minWidth: 190, maxWidth: 420, defaultWidth: 230 },
  { key: 'target_section', label: 'Target Section', minWidth: 190, maxWidth: 420, defaultWidth: 230 },
  { key: 'external_id', label: 'Blueprint ID', minWidth: 130, maxWidth: 240, defaultWidth: 150 },
  { key: 'products', label: 'Products', minWidth: 110, maxWidth: 180, defaultWidth: 120 },
  { key: 'updated', label: 'Updated', minWidth: 170, maxWidth: 320, defaultWidth: 200 },
];

function formatDate(value) {
  if (!value) {
    return '-';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '-';
  }
  return date.toLocaleString();
}

function getBlueprintImage(blueprint) {
  if (blueprint?.thumbnail_url) {
    return blueprint.thumbnail_url;
  }
  const images = Array.isArray(blueprint?.images) ? blueprint.images : [];
  return images.find(Boolean) || '';
}

function labelOrDash(value) {
  const text = String(value || '').trim();
  return text || '-';
}

function shopLabel(shop) {
  return labelOrDash(shop?.title || shop?.name || shop?.external_id);
}

function sectionLabel(section) {
  if (!section) {
    return '-';
  }
  const shopTitle = section.shop_title ? ` (${section.shop_title})` : '';
  return `${labelOrDash(section.title || section.external_id)}${shopTitle}`;
}

function idString(value) {
  return value === null || value === undefined ? '' : String(value);
}

function associationKey(value) {
  return String(value || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '');
}

function linkedEtsyShopId(shop) {
  return idString(shop?.etsy_shop_id);
}

function sectionBelongsToShop(section, shop) {
  if (!section || !shop) {
    return false;
  }
  const shopEtsyShopId = linkedEtsyShopId(shop);
  if (shopEtsyShopId && idString(section.etsy_shop_id) === shopEtsyShopId) {
    return true;
  }
  if (shopEtsyShopId) {
    return false;
  }
  const shopExternalId = idString(shop.external_id);
  if (shopExternalId && idString(section.shop_external_id) === shopExternalId) {
    return true;
  }
  const shopTitle = associationKey(shop.etsy_shop_title || shop.title || shop.name);
  const sectionShopTitle = associationKey(section.shop_title);
  if (shopTitle && sectionShopTitle && shopTitle === sectionShopTitle) {
    return true;
  }
  return false;
}

function sectionsForShop(sections, shop) {
  if (!shop) {
    return sections;
  }
  const shopEtsyShopId = linkedEtsyShopId(shop);
  if (!shopEtsyShopId) {
    return sections;
  }
  return sections.filter((section) => idString(section.etsy_shop_id) === shopEtsyShopId);
}

function findShopForSection(shops, section) {
  if (!section) {
    return null;
  }
  const sectionShopId = idString(section.etsy_shop_id);
  if (sectionShopId) {
    const exact = shops.find((shop) => linkedEtsyShopId(shop) === sectionShopId);
    if (exact) {
      return exact;
    }
  }
  return shops.find((shop) => sectionBelongsToShop(section, shop)) || null;
}

function tagNames(tags) {
  return (Array.isArray(tags) ? tags : [])
    .map((tag) => String(tag?.name || '').trim())
    .filter(Boolean);
}

function normalizeTagInput(values) {
  const seen = new Set();
  const names = [];
  (Array.isArray(values) ? values : String(values || '').split(',')).forEach((value) => {
    const name = String(value || '').trim().replace(/\s+/g, ' ');
    const key = name.toLowerCase();
    if (!name || seen.has(key)) {
      return;
    }
    seen.add(key);
    names.push(name);
  });
  return names;
}

const PrintifyCatalogPage = () => {
  const { fetchWithAuth } = useAuth();
  const [items, setItems] = React.useState([]);
  const [total, setTotal] = React.useState(0);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(defaultRowsPerPage);
  const [search, setSearch] = React.useState('');
  const [debouncedSearch, setDebouncedSearch] = React.useState('');
  const [accounts, setAccounts] = React.useState([]);
  const [shops, setShops] = React.useState([]);
  const [etsySections, setEtsySections] = React.useState([]);
  const [selectedAccountId, setSelectedAccountId] = React.useState('');
  const [selectedTargetShopId, setSelectedTargetShopId] = React.useState('');
  const [selectedTargetSectionId, setSelectedTargetSectionId] = React.useState('');
  const [tagOptions, setTagOptions] = React.useState([]);
  const [selectedTagId, setSelectedTagId] = React.useState('');
  const [selectedIds, setSelectedIds] = React.useState(() => new Set());
  const [actionsAnchorEl, setActionsAnchorEl] = React.useState(null);
  const [tagDialogMode, setTagDialogMode] = React.useState('');
  const [tagDialogTags, setTagDialogTags] = React.useState([]);
  const [tagActionLoading, setTagActionLoading] = React.useState(false);
  const [actionMessage, setActionMessage] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [detailOpen, setDetailOpen] = React.useState(false);
  const [detailBlueprint, setDetailBlueprint] = React.useState(null);
  const [detailLoading, setDetailLoading] = React.useState(false);
  const [planningSaving, setPlanningSaving] = React.useState(false);
  const { getWidth, startColumnResize, tableMinWidth } = useResizableColumns(TABLE_COLUMNS, {
    storageKey: 'table.printify-catalog',
  });

  React.useEffect(() => {
    const handle = window.setTimeout(() => setDebouncedSearch(search.trim()), debounceDelay);
    return () => window.clearTimeout(handle);
  }, [search]);

  React.useEffect(() => {
    setPage(0);
  }, [debouncedSearch, rowsPerPage, selectedAccountId, selectedTagId, selectedTargetShopId, selectedTargetSectionId]);

  const loadPlanningOptions = React.useCallback(async () => {
    try {
      const [accountsResp, shopsResp, etsyAccountsResp] = await Promise.all([
        fetchWithAuth(`${config.apiBaseUrl}/printify/accounts`),
        fetchWithAuth(`${config.apiBaseUrl}/printify/shops`),
        fetchWithAuth(`${config.apiBaseUrl}/etsy/accounts`),
      ]);
      if (!accountsResp.ok) {
        throw new Error('Failed to load Printify accounts');
      }
      if (!shopsResp.ok) {
        throw new Error('Failed to load Printify shops');
      }
      if (!etsyAccountsResp.ok) {
        throw new Error('Failed to load Etsy shops');
      }
      const [accountsPayload, shopsPayload, etsyAccountsPayload] = await Promise.all([
        accountsResp.json(),
        shopsResp.json(),
        etsyAccountsResp.json(),
      ]);
      const nextAccounts = Array.isArray(accountsPayload.items) ? accountsPayload.items : [];
      const etsyShops = (Array.isArray(etsyAccountsPayload.items) ? etsyAccountsPayload.items : [])
        .map((account) => account.shop)
        .filter((shop) => shop?.id)
        .map((shop) => ({
          id: shop.id,
          external_id: shop.external_id,
          title: shop.title || shop.name || String(shop.external_id || ''),
        }));
      const etsyShopById = new Map(etsyShops.map((shop) => [String(shop.id), shop]));
      const nextShops = (Array.isArray(shopsPayload.items) ? shopsPayload.items : [])
        .filter((shop) => !shop.is_template && shop.active !== false)
        .map((shop) => {
          const etsyShop = etsyShopById.get(String(shop.etsy_shop_id || ''));
          return {
            ...shop,
            etsy_shop_id: shop.etsy_shop_id || '',
            etsy_shop_title: etsyShop?.title || '',
          };
        });

      const sectionGroups = await Promise.all(
        etsyShops.map(async (shop) => {
          try {
            const params = new URLSearchParams({ shop_id: shop.id });
            const resp = await fetchWithAuth(`${config.apiBaseUrl}/etsy/sections?${params.toString()}`);
            if (!resp.ok) {
              return [];
            }
            const payload = await resp.json();
            const list = Array.isArray(payload.items) ? payload.items : [];
            return list.map((section) => ({
              ...section,
              etsy_shop_id: section.etsy_shop_id || shop.id,
              shop_title: shop.title,
              shop_external_id: shop.external_id,
            }));
          } catch {
            return [];
          }
        }),
      );
      const nextSections = sectionGroups.flat().sort((a, b) => (
        sectionLabel(a).localeCompare(sectionLabel(b))
      ));

      setAccounts(nextAccounts);
      setShops(nextShops);
      setEtsySections(nextSections);
      setSelectedAccountId((current) => (
        current && nextAccounts.some((account) => account.id === current) ? current : ''
      ));
      setSelectedTargetShopId((current) => (
        current && nextShops.some((shop) => shop.id === current) ? current : ''
      ));
      setSelectedTargetSectionId((current) => (
        current && nextSections.some((section) => section.id === current) ? current : ''
      ));
    } catch (err) {
      setError(err.message || 'Failed to load planning options');
    }
  }, [fetchWithAuth]);

  React.useEffect(() => {
    loadPlanningOptions();
  }, [loadPlanningOptions]);

  const loadTags = React.useCallback(async () => {
    try {
      const response = await fetchWithAuth(`${config.apiBaseUrl}/printify/catalog/tags`);
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.error || 'Failed to load tags');
      }
      const payload = await response.json();
      const nextTags = Array.isArray(payload.items) ? payload.items : [];
      setTagOptions(nextTags);
      setSelectedTagId((current) => (
        current && nextTags.some((tag) => tag.id === current) ? current : ''
      ));
    } catch (err) {
      setError(err.message || 'Failed to load tags');
    }
  }, [fetchWithAuth]);

  React.useEffect(() => {
    loadTags();
  }, [loadTags]);

  const loadCatalog = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.set('limit', rowsPerPage.toString());
      params.set('offset', (page * rowsPerPage).toString());
      if (debouncedSearch) {
        params.set('q', debouncedSearch);
      }
      if (selectedAccountId) {
        params.set('account_id', selectedAccountId);
      }
      if (selectedTagId) {
        params.set('tag_id', selectedTagId);
      }
      if (selectedTargetShopId) {
        params.set('target_printify_shop_id', selectedTargetShopId);
      }
      if (selectedTargetSectionId) {
        params.set('target_etsy_section_id', selectedTargetSectionId);
      }
      const response = await fetchWithAuth(`${config.apiBaseUrl}/printify/catalog?${params.toString()}`);
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.error || 'Failed to load Printify catalog');
      }
      const payload = await response.json();
      setItems(Array.isArray(payload.items) ? payload.items : []);
      setTotal(typeof payload.total === 'number' ? payload.total : 0);
    } catch (err) {
      setError(err.message || 'Failed to load Printify catalog');
    } finally {
      setLoading(false);
    }
  }, [
    debouncedSearch,
    fetchWithAuth,
    page,
    rowsPerPage,
    selectedAccountId,
    selectedTagId,
    selectedTargetShopId,
    selectedTargetSectionId,
  ]);

  React.useEffect(() => {
    loadCatalog();
  }, [loadCatalog]);

  React.useEffect(() => {
    const visible = new Set(items.map((item) => item.id));
    setSelectedIds((previous) => {
      const next = new Set(Array.from(previous).filter((id) => visible.has(id)));
      return next.size === previous.size ? previous : next;
    });
  }, [items]);

  const fetchBlueprintDetail = React.useCallback(async (blueprintId) => {
    const response = await fetchWithAuth(`${config.apiBaseUrl}/printify/catalog/${blueprintId}`);
    if (!response.ok) {
      const payload = await response.json().catch(() => ({}));
      throw new Error(payload.error || 'Failed to load catalog item');
    }
    const payload = await response.json();
    return payload.item || null;
  }, [fetchWithAuth]);

  const refreshOpenDetail = React.useCallback(async () => {
    if (!detailBlueprint?.id) {
      return;
    }
    setDetailLoading(true);
    try {
      const item = await fetchBlueprintDetail(detailBlueprint.id);
      if (item) {
        setDetailBlueprint(item);
      }
    } catch (err) {
      setError(err.message || 'Failed to load catalog item');
    } finally {
      setDetailLoading(false);
    }
  }, [detailBlueprint?.id, fetchBlueprintDetail]);

  const openDetail = async (blueprint) => {
    if (!blueprint?.id) {
      return;
    }
    setDetailOpen(true);
    setDetailBlueprint(blueprint);
    setDetailLoading(true);
    try {
      const item = await fetchBlueprintDetail(blueprint.id);
      if (item) {
        setDetailBlueprint(item);
      }
    } catch (err) {
      setError(err.message || 'Failed to load catalog item');
    } finally {
      setDetailLoading(false);
    }
  };

  const visibleIds = React.useMemo(() => items.map((item) => item.id).filter(Boolean), [items]);
  const selectedVisibleCount = React.useMemo(
    () => visibleIds.filter((id) => selectedIds.has(id)).length,
    [selectedIds, visibleIds],
  );
  const allSelected = visibleIds.length > 0 && selectedVisibleCount === visibleIds.length;
  const indeterminate = selectedVisibleCount > 0 && selectedVisibleCount < visibleIds.length;
  const selectedCount = selectedIds.size;
  const actionsOpen = Boolean(actionsAnchorEl);
  const targetShopOptions = React.useMemo(() => {
    if (!selectedAccountId) {
      return shops;
    }
    return shops.filter((shop) => !shop.account_id || shop.account_id === selectedAccountId);
  }, [selectedAccountId, shops]);
  const selectedTargetShop = React.useMemo(
    () => targetShopOptions.find((shop) => shop.id === selectedTargetShopId) || null,
    [selectedTargetShopId, targetShopOptions],
  );
  const targetSectionOptions = React.useMemo(() => (
    selectedTargetShopId ? sectionsForShop(etsySections, selectedTargetShop) : etsySections
  ), [etsySections, selectedTargetShop, selectedTargetShopId]);

  React.useEffect(() => {
    if (!selectedTargetShopId || targetShopOptions.some((shop) => shop.id === selectedTargetShopId)) {
      return;
    }
    setSelectedTargetShopId('');
  }, [selectedTargetShopId, targetShopOptions]);

  React.useEffect(() => {
    if (!selectedTargetShopId || !selectedTargetSectionId) {
      return;
    }
    if (targetSectionOptions.some((section) => section.id === selectedTargetSectionId)) {
      return;
    }
    setSelectedTargetSectionId('');
  }, [selectedTargetSectionId, selectedTargetShopId, targetSectionOptions]);

  const handleTargetShopFilterChange = (event) => {
    setSelectedTargetShopId(event.target.value);
  };

  const handleTargetSectionFilterChange = (event) => {
    const nextSectionId = event.target.value;
    setSelectedTargetSectionId(nextSectionId);
    if (selectedTargetShopId || !nextSectionId) {
      return;
    }
    const section = etsySections.find((item) => item.id === nextSectionId);
    const shop = findShopForSection(targetShopOptions, section);
    if (shop?.id) {
      setSelectedTargetShopId(shop.id);
    }
  };

  const handleToggleAll = (event) => {
    const checked = event.target.checked;
    setSelectedIds((previous) => {
      const next = new Set(previous);
      visibleIds.forEach((id) => {
        if (checked) {
          next.add(id);
        } else {
          next.delete(id);
        }
      });
      return next;
    });
  };

  const handleToggleRow = (id) => {
    setSelectedIds((previous) => {
      const next = new Set(previous);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const closeActionsMenu = () => setActionsAnchorEl(null);

  const openTagDialog = (mode) => {
    closeActionsMenu();
    setTagDialogMode(mode);
    setTagDialogTags([]);
  };

  const closeTagDialog = () => {
    if (tagActionLoading) {
      return;
    }
    setTagDialogMode('');
    setTagDialogTags([]);
  };

  const runTagAction = React.useCallback(async (blueprintIds, tags, mode) => {
    const cleanTags = normalizeTagInput(tags);
    if (blueprintIds.length === 0 || cleanTags.length === 0) {
      return;
    }
    setTagActionLoading(true);
    setError(null);
    setActionMessage(null);
    try {
      const endpoint = mode === 'remove' ? 'remove' : 'apply';
      const response = await fetchWithAuth(`${config.apiBaseUrl}/printify/catalog/tags/${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ blueprint_ids: blueprintIds, tags: cleanTags }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload.error || 'Failed to update tags');
      }
      setActionMessage({
        type: 'success',
        text: mode === 'remove' ? 'Tag removed from selected blueprints.' : 'Tag applied to selected blueprints.',
      });
      closeTagDialog();
      await loadTags();
      await loadCatalog();
      if (detailBlueprint?.id && blueprintIds.includes(detailBlueprint.id)) {
        await refreshOpenDetail();
      }
    } catch (err) {
      setError(err.message || 'Failed to update tags');
    } finally {
      setTagActionLoading(false);
    }
  }, [detailBlueprint?.id, fetchWithAuth, loadCatalog, loadTags, refreshOpenDetail]);

  const submitTagDialog = () => {
    runTagAction(Array.from(selectedIds), tagDialogTags, tagDialogMode);
  };

  const updateBlueprintPlanning = React.useCallback(async (blueprintId, payload) => {
    if (!blueprintId) {
      return;
    }
    setPlanningSaving(true);
    setError(null);
    setActionMessage(null);
    try {
      const response = await fetchWithAuth(`${config.apiBaseUrl}/printify/catalog/${blueprintId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const responsePayload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(responsePayload.error || 'Failed to update catalog planning');
      }
      const nextBlueprint = responsePayload.item;
      if (nextBlueprint) {
        setDetailBlueprint(nextBlueprint);
        setItems((previous) => previous.map((item) => (
          item.id === nextBlueprint.id ? { ...item, ...nextBlueprint } : item
        )));
      }
      setActionMessage({ type: 'success', text: 'Catalog planning updated.' });
    } catch (err) {
      setError(err.message || 'Failed to update catalog planning');
    } finally {
      setPlanningSaving(false);
    }
  }, [fetchWithAuth]);

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, height: '100%' }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
        <TextField
          select
          label="Account"
          value={selectedAccountId}
          onChange={(event) => setSelectedAccountId(event.target.value)}
          sx={{ minWidth: 220 }}
        >
          <MenuItem value="">All accounts</MenuItem>
          {accounts.map((account) => (
            <MenuItem key={account.id} value={account.id}>
              {account.name || account.id}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          fullWidth
          placeholder="Search catalog by title, product type, or blueprint ID"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          InputProps={{
            startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />,
          }}
        />
        <TextField
          select
          label="Tag"
          value={selectedTagId}
          onChange={(event) => setSelectedTagId(event.target.value)}
          sx={{ minWidth: 190 }}
        >
          <MenuItem value="">All tags</MenuItem>
          {tagOptions.map((tag) => (
            <MenuItem key={tag.id} value={tag.id}>
              {tag.name}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Target Shop"
          value={selectedTargetShopId}
          onChange={handleTargetShopFilterChange}
          sx={{ minWidth: 210 }}
        >
          <MenuItem value="">All target shops</MenuItem>
          {targetShopOptions.map((shop) => (
            <MenuItem key={shop.id} value={shop.id}>
              {shopLabel(shop)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Target Section"
          value={selectedTargetSectionId}
          onChange={handleTargetSectionFilterChange}
          sx={{ minWidth: 230 }}
        >
          <MenuItem value="">All target sections</MenuItem>
          {targetSectionOptions.map((section) => (
            <MenuItem key={section.id} value={section.id}>
              {sectionLabel(section)}
            </MenuItem>
          ))}
        </TextField>
        <Button
          variant="outlined"
          endIcon={<KeyboardArrowDownIcon />}
          onClick={(event) => setActionsAnchorEl(event.currentTarget)}
          disabled={selectedCount === 0 || tagActionLoading}
          sx={{ whiteSpace: 'nowrap' }}
        >
          Actions
        </Button>
        <Menu anchorEl={actionsAnchorEl} open={actionsOpen} onClose={closeActionsMenu}>
          <MenuItem onClick={() => openTagDialog('apply')}>Apply Tag</MenuItem>
          <MenuItem onClick={() => openTagDialog('remove')}>Remove Tag</MenuItem>
        </Menu>
        <Button
          variant="outlined"
          startIcon={loading ? <CircularProgress color="inherit" size={16} /> : <RefreshIcon />}
          onClick={loadCatalog}
          disabled={loading}
          sx={{ whiteSpace: 'nowrap' }}
        >
          Refresh
        </Button>
      </Stack>

      {error ? <Alert severity="error">{error}</Alert> : null}
      {actionMessage ? <Alert severity={actionMessage.type}>{actionMessage.text}</Alert> : null}

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Checkbox
            size="small"
            indeterminate={indeterminate}
            checked={allSelected}
            onChange={handleToggleAll}
            disabled={visibleIds.length === 0}
          />
          <Typography variant="body2" color="text.secondary">
            {selectedCount > 0 ? `${selectedCount} selected` : 'Select visible'}
          </Typography>
        </Stack>
      </Box>

      <Paper sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <TableContainer sx={{ flex: 1, overflowY: 'auto' }}>
          <Table size="small" sx={{ tableLayout: 'fixed', minWidth: tableMinWidth }} stickyHeader>
            <ResizableColGroup columns={TABLE_COLUMNS} getWidth={getWidth} />
            <TableHead>
              <TableRow>
                {TABLE_COLUMNS.map((column) => {
                  if (column.key === 'select') {
                    return (
                      <TableCell
                        key={column.key}
                        padding="checkbox"
                        sx={{
                          width: getWidth(column.key),
                          minWidth: getWidth(column.key),
                          maxWidth: getWidth(column.key),
                        }}
                      >
                        <Checkbox
                          indeterminate={indeterminate}
                          checked={allSelected}
                          onChange={handleToggleAll}
                          disabled={visibleIds.length === 0}
                        />
                      </TableCell>
                    );
                  }
                  return (
                    <ResizableHeaderCell
                      key={column.key}
                      column={column}
                      getWidth={getWidth}
                      onResizeStart={startColumnResize}
                    />
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableLoadingRow colSpan={TABLE_COLUMNS.length} label="Loading Printify catalog..." />
              ) : items.length === 0 ? (
                <TableEmptyRow colSpan={TABLE_COLUMNS.length} label="No catalog items found." />
              ) : (
                items.map((blueprint) => (
                  <TableRow
                    key={blueprint.id}
                    hover
                    tabIndex={0}
                    sx={{ cursor: 'pointer' }}
                    onClick={() => openDetail(blueprint)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        openDetail(blueprint);
                      }
                    }}
                    aria-label={`Open catalog item ${blueprint.title || blueprint.external_id}`}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedIds.has(blueprint.id)}
                        onClick={(event) => event.stopPropagation()}
                        onChange={() => handleToggleRow(blueprint.id)}
                        inputProps={{ 'aria-label': `Select ${blueprint.title || blueprint.external_id}` }}
                      />
                    </TableCell>
                    <TableCell>
                      <BlueprintThumb blueprint={blueprint} size={72} />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="subtitle2" noWrap title={blueprint.title}>
                          {labelOrDash(blueprint.title)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {Array.isArray(blueprint.images) ? blueprint.images.length : 0} images
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <TagChips tags={blueprint.tags} limit={3} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap title={blueprint.product_type}>
                        {labelOrDash(blueprint.product_type)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap title={shopLabel(blueprint.source_shop)}>
                        {shopLabel(blueprint.source_shop)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap title={shopLabel(blueprint.target_shop)}>
                        {shopLabel(blueprint.target_shop)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap title={sectionLabel(blueprint.target_section)}>
                        {sectionLabel(blueprint.target_section)}
                      </Typography>
                    </TableCell>
                    <TableCell>{blueprint.external_id || '-'}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        icon={<Inventory2Icon fontSize="small" />}
                        label={blueprint.product_count || 0}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>{formatDate(blueprint.updated_at)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(event, nextPage) => setPage(nextPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Paper>

      <PrintifyCatalogDetailDialog
        open={detailOpen}
        blueprint={detailBlueprint}
        loading={detailLoading}
        tagOptions={tagOptions}
        tagActionLoading={tagActionLoading}
        shops={shops}
        etsySections={etsySections}
        planningSaving={planningSaving}
        onUpdatePlanning={(payload) => {
          if (detailBlueprint?.id) {
            updateBlueprintPlanning(detailBlueprint.id, payload);
          }
        }}
        onApplyTags={(tags) => {
          if (detailBlueprint?.id) {
            runTagAction([detailBlueprint.id], tags, 'apply');
          }
        }}
        onRemoveTag={(tagName) => {
          if (detailBlueprint?.id) {
            runTagAction([detailBlueprint.id], [tagName], 'remove');
          }
        }}
        onClose={() => setDetailOpen(false)}
      />
      <TagActionDialog
        open={Boolean(tagDialogMode)}
        mode={tagDialogMode}
        selectedCount={selectedCount}
        tags={tagDialogTags}
        tagOptions={tagOptions}
        loading={tagActionLoading}
        onTagsChange={setTagDialogTags}
        onClose={closeTagDialog}
        onSubmit={submitTagDialog}
      />
    </Box>
  );
};

const TagChips = ({ tags, limit = 6, onDelete, disabled = false }) => {
  const items = Array.isArray(tags) ? tags : [];
  if (items.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        -
      </Typography>
    );
  }
  const visible = items.slice(0, limit);
  const hidden = items.length - visible.length;
  return (
    <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
      {visible.map((tag) => (
        <Chip
          key={tag.id || tag.name}
          size="small"
          icon={<LocalOfferIcon fontSize="small" />}
          label={tag.name}
          variant="outlined"
          disabled={disabled}
          onDelete={onDelete ? () => onDelete(tag.name) : undefined}
        />
      ))}
      {hidden > 0 ? <Chip size="small" label={`+${hidden}`} variant="outlined" /> : null}
    </Stack>
  );
};

const TagActionDialog = ({
  open,
  mode,
  selectedCount,
  tags,
  tagOptions,
  loading,
  onTagsChange,
  onClose,
  onSubmit,
}) => {
  const cleanTags = normalizeTagInput(tags);
  const isRemove = mode === 'remove';
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{isRemove ? 'Remove Tag' : 'Apply Tag'}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ pt: 0.5 }}>
          <Typography variant="body2" color="text.secondary">
            {isRemove ? 'Remove tags from' : 'Apply tags to'} {selectedCount} selected blueprint{selectedCount === 1 ? '' : 's'}.
          </Typography>
          <Autocomplete
            multiple
            freeSolo
            options={tagOptions.map((tag) => tag.name)}
            value={tags}
            onChange={(event, value) => onTagsChange(normalizeTagInput(value))}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Tags"
                placeholder="Type a tag and press Enter"
                autoFocus
              />
            )}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button
          variant="contained"
          color={isRemove ? 'warning' : 'primary'}
          disabled={selectedCount === 0 || cleanTags.length === 0 || loading}
          startIcon={loading ? <CircularProgress color="inherit" size={16} /> : <LocalOfferIcon />}
          onClick={onSubmit}
        >
          {loading ? 'Saving...' : isRemove ? 'Remove Tag' : 'Apply Tag'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const BlueprintThumb = ({ blueprint, size = 72 }) => {
  const src = getBlueprintImage(blueprint);
  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: 1,
        border: (theme) => `1px solid ${theme.palette.divider}`,
        bgcolor: 'background.default',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      {src ? (
        <Box
          component="img"
          src={src}
          alt={blueprint?.title ? `${blueprint.title} catalog preview` : 'Catalog preview'}
          sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      ) : (
        <CategoryIcon color="disabled" />
      )}
    </Box>
  );
};

const PrintifyCatalogDetailDialog = ({
  open,
  blueprint,
  loading,
  tagOptions,
  tagActionLoading,
  shops,
  etsySections,
  planningSaving,
  onUpdatePlanning,
  onApplyTags,
  onRemoveTag,
  onClose,
}) => {
  const [tab, setTab] = React.useState('overview');
  const [addTags, setAddTags] = React.useState([]);
  const [draftProductType, setDraftProductType] = React.useState('');
  const [draftTargetShopId, setDraftTargetShopId] = React.useState('');
  const [draftTargetSectionId, setDraftTargetSectionId] = React.useState('');

  React.useEffect(() => {
    setTab('overview');
    setAddTags([]);
    setDraftProductType(blueprint?.product_type || '');
    setDraftTargetShopId(blueprint?.target_shop?.id || '');
    setDraftTargetSectionId(blueprint?.target_section?.id || '');
  }, [blueprint?.id]);

  const products = Array.isArray(blueprint?.products) ? blueprint.products : [];
  const images = Array.isArray(blueprint?.images) ? blueprint.images : [];
  const tags = Array.isArray(blueprint?.tags) ? blueprint.tags : [];
  const cleanAddTags = normalizeTagInput(addTags);
  const shopOptions = React.useMemo(() => {
    const accountId = blueprint?.account?.id || '';
    if (!accountId) {
      return shops;
    }
    const filtered = shops.filter((shop) => !shop.account_id || shop.account_id === accountId);
    const selected = shops.find((shop) => shop.id === draftTargetShopId);
    if (selected && !filtered.some((shop) => shop.id === selected.id)) {
      return [selected, ...filtered];
    }
    return filtered;
  }, [blueprint?.account?.id, draftTargetShopId, shops]);
  const draftTargetShop = React.useMemo(
    () => shopOptions.find((shop) => shop.id === draftTargetShopId) || null,
    [draftTargetShopId, shopOptions],
  );
  const draftSectionOptions = React.useMemo(() => (
    draftTargetShopId ? sectionsForShop(etsySections, draftTargetShop) : etsySections
  ), [draftTargetShop, draftTargetShopId, etsySections]);

  React.useEffect(() => {
    if (!draftTargetShopId || !draftTargetSectionId) {
      return;
    }
    if (draftSectionOptions.some((section) => section.id === draftTargetSectionId)) {
      return;
    }
    setDraftTargetSectionId('');
  }, [draftSectionOptions, draftTargetSectionId, draftTargetShopId]);

  const handleApplyTags = () => {
    if (cleanAddTags.length === 0) {
      return;
    }
    onApplyTags?.(cleanAddTags);
    setAddTags([]);
  };

  const handleDraftTargetShopChange = (event) => {
    setDraftTargetShopId(event.target.value);
  };

  const handleDraftTargetSectionChange = (event) => {
    const nextSectionId = event.target.value;
    setDraftTargetSectionId(nextSectionId);
    if (draftTargetShopId || !nextSectionId) {
      return;
    }
    const section = etsySections.find((item) => item.id === nextSectionId);
    const shop = findShopForSection(shopOptions, section);
    if (shop?.id) {
      setDraftTargetShopId(shop.id);
    }
  };

  const planningChanged = Boolean(blueprint) && (
    draftProductType.trim() !== (blueprint?.product_type || '')
    || draftTargetShopId !== (blueprint?.target_shop?.id || '')
    || draftTargetSectionId !== (blueprint?.target_section?.id || '')
  );

  const handleSavePlanning = () => {
    if (!planningChanged) {
      return;
    }
    onUpdatePlanning?.({
      product_type: draftProductType,
      target_printify_shop_id: draftTargetShopId,
      target_etsy_section_id: draftTargetSectionId,
    });
  };

  const left = loading ? (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 240 }}>
      <CircularProgress size={24} />
    </Box>
  ) : blueprint ? (
    <Stack spacing={2}>
      <Tabs value={tab} onChange={(event, next) => setTab(next)} aria-label="Printify catalog details">
        <Tab value="overview" label="Overview" />
        <Tab value="products" label="Products" />
        <Tab value="shipping" label="Shipping" />
        <Tab value="data" label="Data" />
      </Tabs>
      {tab === 'overview' ? <BlueprintOverview blueprint={blueprint} images={images} /> : null}
      {tab === 'products' ? <BlueprintProducts products={products} productCount={blueprint.product_count || 0} /> : null}
      {tab === 'shipping' ? <BlueprintShipping shipping={blueprint.shipping} /> : null}
      {tab === 'data' ? <JsonTreeViewer data={blueprint.raw_data || blueprint} /> : null}
    </Stack>
  ) : (
    <Typography color="text.secondary">No catalog item selected.</Typography>
  );

  const right = blueprint ? (
    <Stack spacing={2}>
      <BlueprintThumb blueprint={blueprint} size={220} />
      <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
        <Chip label={`${blueprint.product_count || 0} products`} color="primary" variant="outlined" />
        <Chip label={`${images.length} images`} variant="outlined" />
        <Chip label={`${tags.length} tags`} variant="outlined" />
      </Stack>
      <Divider />
      <Box>
        <Typography variant="subtitle2" gutterBottom>
          Tags
        </Typography>
        <TagChips tags={tags} onDelete={onRemoveTag} disabled={tagActionLoading} />
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1.5 }}>
          <Autocomplete
            multiple
            freeSolo
            fullWidth
            size="small"
            options={tagOptions.map((tag) => tag.name)}
            value={addTags}
            onChange={(event, value) => setAddTags(normalizeTagInput(value))}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Add tags"
                placeholder="Type and press Enter"
              />
            )}
          />
          <Button
            variant="contained"
            size="small"
            startIcon={tagActionLoading ? <CircularProgress color="inherit" size={16} /> : <LocalOfferIcon />}
            disabled={cleanAddTags.length === 0 || tagActionLoading}
            onClick={handleApplyTags}
            sx={{ whiteSpace: 'nowrap' }}
          >
            Add
          </Button>
        </Stack>
      </Box>
      <Divider />
      <Box>
        <Typography variant="subtitle2" gutterBottom>
          Publishing Plan
        </Typography>
        <Stack spacing={1.5}>
          <TextField
            size="small"
            label="Product Type"
            value={draftProductType}
            onChange={(event) => setDraftProductType(event.target.value)}
            placeholder="Racerback Dress"
            disabled={planningSaving}
          />
          <TextField
            select
            size="small"
            label="Target Printify Shop"
            value={draftTargetShopId}
            onChange={handleDraftTargetShopChange}
            disabled={planningSaving}
          >
            <MenuItem value="">No target shop</MenuItem>
            {shopOptions.map((shop) => (
              <MenuItem key={shop.id} value={shop.id}>
                {shopLabel(shop)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label="Target Etsy Section"
            value={draftTargetSectionId}
            onChange={handleDraftTargetSectionChange}
            disabled={planningSaving}
          >
            <MenuItem value="">No target section</MenuItem>
            {draftSectionOptions.map((section) => (
              <MenuItem key={section.id} value={section.id}>
                {sectionLabel(section)}
              </MenuItem>
            ))}
          </TextField>
          <Button
            variant="contained"
            size="small"
            disabled={!planningChanged || planningSaving}
            startIcon={planningSaving ? <CircularProgress color="inherit" size={16} /> : null}
            onClick={handleSavePlanning}
            sx={{ alignSelf: 'flex-start' }}
          >
            {planningSaving ? 'Saving...' : 'Save Plan'}
          </Button>
        </Stack>
      </Box>
      <Divider />
      <Box>
        <Typography variant="subtitle2" gutterBottom>
          Catalog
        </Typography>
        <Typography variant="body2">Blueprint ID: {blueprint.external_id || '-'}</Typography>
        <Typography variant="body2">Product type: {labelOrDash(blueprint.product_type)}</Typography>
        <Typography variant="body2">Source shop: {shopLabel(blueprint.source_shop)}</Typography>
      </Box>
      <Divider />
      <Box>
        <Typography variant="subtitle2" gutterBottom>
          Account
        </Typography>
        <Typography variant="body2">{labelOrDash(blueprint.account?.name)}</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
          {blueprint.account?.id || '-'}
        </Typography>
      </Box>
      <Divider />
      <Box>
        <Typography variant="subtitle2" gutterBottom>
          Timeline
        </Typography>
        <Typography variant="body2">Created: {formatDate(blueprint.created_at)}</Typography>
        <Typography variant="body2">Updated: {formatDate(blueprint.updated_at)}</Typography>
      </Box>
    </Stack>
  ) : null;

  return (
    <DetailModalShell
      open={open}
      onClose={onClose}
      title={loading ? 'Loading catalog item...' : blueprint?.title || 'Catalog Item'}
      externalId={blueprint?.external_id || blueprint?.id}
      left={left}
      right={right}
      actions={<Button onClick={onClose}>Close</Button>}
    />
  );
};

const BlueprintOverview = ({ blueprint, images }) => (
  <Stack spacing={2}>
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
      <Metric label="Linked products" value={blueprint.product_count || 0} />
      <Metric label="Images" value={images.length} />
      <Metric label="Tags" value={tagNames(blueprint.tags).length} />
      <Metric label="Product type" value={labelOrDash(blueprint.product_type)} />
      <Metric label="Source shop" value={shopLabel(blueprint.source_shop)} />
      <Metric label="Target shop" value={shopLabel(blueprint.target_shop)} />
      <Metric label="Target section" value={sectionLabel(blueprint.target_section)} />
    </Stack>
    <Divider />
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        Catalog Images
      </Typography>
      {images.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No catalog images are stored for this blueprint.
        </Typography>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gap: 1.5,
            gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
          }}
        >
          {images.map((src, index) => (
            <Paper
              key={`${src}-${index}`}
              variant="outlined"
              sx={{ overflow: 'hidden', borderRadius: 1, aspectRatio: '1 / 1', bgcolor: 'background.default' }}
            >
              <Box
                component="img"
                src={src}
                alt={`${blueprint.title || 'Catalog'} image ${index + 1}`}
                sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              />
            </Paper>
          ))}
        </Box>
      )}
    </Box>
  </Stack>
);

const BlueprintProducts = ({ products, productCount }) => {
  if (products.length === 0) {
    return <Typography color="text.secondary">No local products are linked to this catalog item.</Typography>;
  }
  return (
    <Stack spacing={1.5}>
      {productCount > products.length ? (
        <Alert severity="info">
          Showing the first {products.length} of {productCount} linked products.
        </Alert>
      ) : null}
      {products.map((product) => (
        <Paper key={product.id} variant="outlined" sx={{ p: 1.5 }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <ProductThumb product={product} />
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="subtitle2" noWrap title={product.title}>
                {labelOrDash(product.title)}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap title={product.shop?.title}>
                {product.shop?.title || '-'} - ID: {product.external_id || product.id}
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 0.75, flexWrap: 'wrap', gap: 1 }}>
                {product.product_type ? <Chip size="small" label={product.product_type} variant="outlined" /> : null}
                {product.variant_type ? <Chip size="small" label={product.variant_type} variant="outlined" /> : null}
                <Chip
                  size="small"
                  label={product.status || (product.is_published ? 'Published' : 'Unpublished')}
                  color={product.is_published ? 'success' : 'default'}
                />
              </Stack>
            </Box>
          </Stack>
        </Paper>
      ))}
    </Stack>
  );
};

const BlueprintShipping = ({ shipping }) => {
  const providers = Array.isArray(shipping?.providers) ? shipping.providers : [];
  return (
    <Stack spacing={2}>
      <Alert severity="info">
        Shipping costs shown here come from Printify catalog shipping profiles. First item is the supplier
        shipping charge for one item; additional item is the charge for each extra item in the same profile.
      </Alert>
      {shipping?.error ? <Alert severity="warning">{shipping.error}</Alert> : null}
      {shipping?.providers_error ? <Alert severity="warning">{shipping.providers_error}</Alert> : null}
      {providers.length === 0 ? (
        <Typography color="text.secondary">No shipping profiles are available for this catalog item.</Typography>
      ) : (
        providers.map((provider) => (
          <ShippingProviderCard key={provider.print_provider_id} provider={provider} />
        ))
      )}
    </Stack>
  );
};

const ShippingProviderCard = ({ provider }) => {
  const profiles = Array.isArray(provider?.profiles) ? provider.profiles : [];
  const variants = Array.isArray(provider?.variants) ? provider.variants : [];
  const locationLabel = providerLocationLabel(provider?.location);
  const providerTitle = provider?.title || `Provider ${provider?.print_provider_id || '-'}`;
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={1.5}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }}>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="subtitle1" noWrap title={providerTitle}>
              {providerTitle}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Provider ID: {provider?.print_provider_id || '-'}{locationLabel ? ` - ${locationLabel}` : ''}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
            <Chip size="small" label={`Handling: ${formatHandlingTime(provider?.handling_time)}`} />
            <Chip size="small" label={`${profiles.length} profiles`} variant="outlined" />
            <Chip size="small" label={`${variants.length} variants`} variant="outlined" />
            {provider?.product_count ? <Chip size="small" label={`${provider.product_count} local products`} variant="outlined" /> : null}
          </Stack>
        </Stack>
        {provider?.error ? <Alert severity="warning">{provider.error}</Alert> : null}
        {provider?.variants_error ? <Alert severity="warning">{provider.variants_error}</Alert> : null}
        <ShippingProfileTable profiles={profiles} />
        <ShippingVariantList variants={variants} />
      </Stack>
    </Paper>
  );
};

const ShippingProfileTable = ({ profiles }) => {
  if (!profiles.length) {
    return <Typography color="text.secondary">No shipping profiles returned for this provider.</Typography>;
  }
  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: '30%' }}>Countries</TableCell>
            <TableCell sx={{ width: 140 }}>First Item</TableCell>
            <TableCell sx={{ width: 160 }}>Additional Item</TableCell>
            <TableCell>Variants</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {profiles.map((profile, index) => (
            <TableRow key={`${profile.country_count || 0}-${profile.variant_count || 0}-${index}`}>
              <TableCell sx={{ verticalAlign: 'top' }}>
                <ShippingCountries countries={profile.countries} />
              </TableCell>
              <TableCell sx={{ verticalAlign: 'top' }}>{formatShippingCost(profile.first_item)}</TableCell>
              <TableCell sx={{ verticalAlign: 'top' }}>{formatShippingCost(profile.additional_items)}</TableCell>
              <TableCell sx={{ verticalAlign: 'top' }}>
                <ShippingVariantSummary variants={profile.variants} variantCount={profile.variant_count} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const ShippingCountries = ({ countries }) => {
  const values = Array.isArray(countries) ? countries.filter(Boolean) : [];
  if (!values.length) {
    return <Typography variant="body2">-</Typography>;
  }
  const visible = values.slice(0, 8);
  if (values.length <= visible.length) {
    return <Typography variant="body2">{visible.join(', ')}</Typography>;
  }
  return (
    <Box component="details">
      <Box component="summary" sx={{ cursor: 'pointer', color: 'primary.main', typography: 'body2' }}>
        {values.length} countries: {visible.join(', ')} +{values.length - visible.length}
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
        {values.join(', ')}
      </Typography>
    </Box>
  );
};

const ShippingVariantSummary = ({ variants, variantCount }) => {
  const values = Array.isArray(variants) ? variants : [];
  if (!values.length) {
    return <Typography variant="body2">{variantCount ? `${variantCount} variants` : '-'}</Typography>;
  }
  const visible = values.slice(0, 4);
  return (
    <Stack spacing={0.75}>
      <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', gap: 0.75 }}>
        {visible.map((variant) => (
          <Chip
            key={variantKey(variant)}
            size="small"
            label={variantLabel(variant)}
            variant="outlined"
            title={variantDetailTitle(variant)}
          />
        ))}
        {values.length > visible.length ? <Chip size="small" label={`+${values.length - visible.length}`} variant="outlined" /> : null}
      </Stack>
      {values.length > visible.length ? (
        <Box component="details">
          <Box component="summary" sx={{ cursor: 'pointer', color: 'primary.main', typography: 'caption' }}>
            View all {values.length} variants
          </Box>
          <ShippingVariantGrid variants={values} compact />
        </Box>
      ) : null}
    </Stack>
  );
};

const ShippingVariantList = ({ variants }) => {
  const values = Array.isArray(variants) ? variants : [];
  if (!values.length) {
    return null;
  }
  return (
    <Box component="details">
      <Box component="summary" sx={{ cursor: 'pointer', color: 'primary.main', typography: 'body2' }}>
        Provider variants and print-area settings ({values.length})
      </Box>
      <ShippingVariantGrid variants={values} />
    </Box>
  );
};

const ShippingVariantGrid = ({ variants, compact = false }) => (
  <Box
    sx={{
      display: 'grid',
      gap: 1,
      mt: 1,
      gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
    }}
  >
    {variants.map((variant) => (
      <Paper key={variantKey(variant)} variant="outlined" sx={{ p: compact ? 1 : 1.25 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap title={variantLabel(variant)}>
          {variantLabel(variant)}
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block">
          ID: {variant?.id || '-'}
        </Typography>
        {variantOptionsLabel(variant) ? (
          <Typography variant="caption" color="text.secondary" display="block">
            {variantOptionsLabel(variant)}
          </Typography>
        ) : null}
        {variantPlaceholdersLabel(variant) ? (
          <Typography variant="caption" color="text.secondary" display="block">
            {variantPlaceholdersLabel(variant)}
          </Typography>
        ) : null}
      </Paper>
    ))}
  </Box>
);

const ProductThumb = ({ product }) => {
  const src = product?.thumbnail_url || '';
  return (
    <Box
      sx={{
        width: 64,
        height: 64,
        borderRadius: 1,
        border: (theme) => `1px solid ${theme.palette.divider}`,
        bgcolor: 'background.default',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      {src ? (
        <Box
          component="img"
          src={src}
          alt={product?.title ? `${product.title} mockup` : 'Product mockup'}
          sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      ) : (
        <ImageIcon color="disabled" />
      )}
    </Box>
  );
};

const Metric = ({ label, value }) => (
  <Box sx={{ flex: 1, minWidth: 140 }}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="h6" noWrap title={String(value ?? '-')}>
      {value ?? '-'}
    </Typography>
  </Box>
);

function formatShippingCost(cost) {
  const amount = Number(cost?.cost);
  if (!Number.isFinite(amount)) {
    return '-';
  }
  const currency = String(cost?.currency || 'USD').toUpperCase();
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(amount / 100);
  } catch {
    return `${currency} ${(amount / 100).toFixed(2)}`;
  }
}

function formatHandlingTime(handlingTime) {
  const value = Number(handlingTime?.value);
  const unit = String(handlingTime?.unit || '').trim();
  if (!Number.isFinite(value) || value <= 0) {
    return '-';
  }
  return `${value} ${unit || 'days'}`;
}

function providerLocationLabel(location) {
  if (!location) {
    return '';
  }
  return [location.city, location.region, location.country].filter(Boolean).join(', ');
}

function variantKey(variant) {
  return `${variant?.id || 'variant'}-${variant?.title || ''}`;
}

function variantLabel(variant) {
  const title = String(variant?.title || '').trim();
  return title || `Variant ${variant?.id || '-'}`;
}

function variantDetailTitle(variant) {
  return [variantLabel(variant), variantOptionsLabel(variant), variantPlaceholdersLabel(variant)].filter(Boolean).join('\n');
}

function variantOptionsLabel(variant) {
  const options = variant?.options && typeof variant.options === 'object' ? variant.options : {};
  return Object.entries(options)
    .filter(([, value]) => value !== null && value !== undefined && String(value).trim() !== '')
    .map(([key, value]) => `${key}: ${value}`)
    .join(', ');
}

function variantPlaceholdersLabel(variant) {
  const placeholders = Array.isArray(variant?.placeholders) ? variant.placeholders : [];
  if (!placeholders.length) {
    return '';
  }
  return placeholders
    .map((placeholder) => {
      const size = placeholder?.width && placeholder?.height ? ` ${placeholder.width}x${placeholder.height}` : '';
      return `${placeholder?.position || 'area'}${size}`;
    })
    .join(', ');
}

export default PrintifyCatalogPage;
